import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { rolesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, ShieldCheck, Buildings } from '@phosphor-icons/react';

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', all_clinics: false, permissions: {} });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await rolesAPI.list();
      setRoles(data.roles || []);
      setModules(data.modules || []);
      setActions(data.actions || []);
    } catch (e) {
      toast.error('Failed to load roles');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', all_clinics: false, admin_portal_access: false, permissions: {} });
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name, description: r.description || '',
      all_clinics: !!r.all_clinics, admin_portal_access: !!r.admin_portal_access,
      permissions: JSON.parse(JSON.stringify(r.permissions || {})),
    });
    setDialogOpen(true);
  };

  const hasAction = (mod, act) => (form.permissions[mod] || []).includes(act);
  const toggleAction = (mod, act) => setForm(f => {
    const cur = new Set(f.permissions[mod] || []);
    if (cur.has(act)) cur.delete(act); else cur.add(act);
    return { ...f, permissions: { ...f.permissions, [mod]: Array.from(cur) } };
  });
  const toggleModuleAll = (mod) => setForm(f => {
    const all = (f.permissions[mod] || []).length === actions.length;
    return { ...f, permissions: { ...f.permissions, [mod]: all ? [] : [...actions] } };
  });

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Role name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await rolesAPI.update(editing.id, {
          name: form.name.trim(), description: form.description,
          all_clinics: form.all_clinics, permissions: form.permissions,
          ...(editing.scope === 'admin' ? { admin_portal_access: form.admin_portal_access } : {}),
        });
        toast.success('Role updated');
      } else {
        await rolesAPI.create({
          name: form.name.trim(), description: form.description,
          all_clinics: form.all_clinics, permissions: form.permissions,
        });
        toast.success('Role created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save role');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await rolesAPI.remove(deleteConfirm.id);
      toast.success('Role deleted');
      setDeleteConfirm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div data-testid="roles-page">
      <PageHeader
        title="Roles & Permissions"
        subtitle="Define what each role can do. Assign roles to employees under Team Access."
        actions={
          <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" onClick={openCreate} data-testid="add-role-btn">
            <Plus size={16} className="mr-2" /> New Role
          </Button>
        }
      />

      <div className="page-content">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {loading ? (
            [...Array(6)].map((_, i) => <div key={i} className="skeleton h-32 rounded-xl" />)
          ) : roles.map(r => (
            <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex flex-col" data-testid={`role-card-${r.key}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-[#EBF3FA] flex items-center justify-center">
                    <ShieldCheck size={18} className="text-[#0F4C81]" />
                  </div>
                  <div>
                    <div className="font-semibold text-[#0F172A] font-['Plus_Jakarta_Sans']">{r.name}</div>
                    <div className="text-xs text-[#64748B]">{r.assigned_count || 0} assigned</div>
                  </div>
                </div>
                {r.is_system && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Built-in</span>}
              </div>
              <p className="text-sm text-[#64748B] mt-3 flex-1">{r.description}</p>
              <div className="flex items-center gap-2 mt-3">
                {r.all_clinics
                  ? <span className="inline-flex items-center gap-1 text-xs text-[#0F4C81]"><Buildings size={13} /> All clinics</span>
                  : <span className="inline-flex items-center gap-1 text-xs text-[#64748B]"><Buildings size={13} /> Allocated clinics only</span>}
                {r.scope === 'admin' && (
                  r.admin_portal_access
                    ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#EBF3FA] text-[#0F4C81]" data-testid={`portal-on-${r.key}`}><ShieldCheck size={12} /> Admin portal: On</span>
                    : <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-500" data-testid={`portal-off-${r.key}`}>Admin portal: Off</span>
                )}
              </div>
              <div className="flex items-center gap-1 mt-3 pt-3 border-t border-[#E2E8F0]">
                <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => openEdit(r)} data-testid={`edit-role-${r.key}`}>
                  <PencilSimple size={14} className="mr-1" /> Edit Permissions
                </Button>
                {!r.is_system && (
                  <Button variant="ghost" size="sm" className="h-8 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setDeleteConfirm(r)} data-testid={`delete-role-${r.key}`}>
                    <Trash size={14} className="mr-1" /> Delete
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">{editing ? `Edit Role — ${editing.name}` : 'New Role'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Role Name</label>
              <Input value={form.name} disabled={editing?.is_system} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Front Desk" data-testid="role-name-input" />
            </div>
            <div>
              <label className="form-label">Description</label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Short description" data-testid="role-desc-input" />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer mt-3">
            <input type="checkbox" checked={form.all_clinics} onChange={e => setForm(f => ({ ...f, all_clinics: e.target.checked }))}
              className="rounded border-gray-300" data-testid="role-all-clinics" />
            <span className="text-sm text-[#0F172A]">Access to <strong>all clinics</strong> (otherwise limited to allocated clinics)</span>
          </label>

          {editing?.scope === 'admin' && (
            <label className="flex items-start gap-2 cursor-pointer mt-3 p-3 rounded-lg bg-[#EBF3FA] border border-[#EBF3FA]">
              <input type="checkbox" checked={form.admin_portal_access}
                disabled={editing?.key === 'adm_admin'}
                onChange={e => setForm(f => ({ ...f, admin_portal_access: e.target.checked }))}
                className="rounded border-gray-300 mt-0.5" data-testid="role-admin-portal-access" />
              <span className="text-sm text-[#0F172A]">
                Allow <strong>Admin Portal</strong> sign-in
                <span className="block text-xs text-[#64748B] mt-0.5">
                  When off, users with this role cannot log into the admin portal (they use the Clinic Portal instead).
                  {editing?.key === 'adm_admin' && ' The Admin role always keeps access.'}
                </span>
              </span>
            </label>
          )}

          <div className="mt-4 border border-[#E2E8F0] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8FAFC] text-[#64748B] text-xs">
                  <th className="text-left p-2 pl-3">Module</th>
                  {actions.map(a => <th key={a} className="text-center p-2 capitalize w-16">{a}</th>)}
                  <th className="text-center p-2 w-14">All</th>
                </tr>
              </thead>
              <tbody>
                {modules.map(m => {
                  const allOn = (form.permissions[m.key] || []).length === actions.length;
                  return (
                    <tr key={m.key} className="border-t border-[#E2E8F0]" data-testid={`perm-row-${m.key}`}>
                      <td className="p-2 pl-3 text-[#0F172A]">{m.label}</td>
                      {actions.map(a => (
                        <td key={a} className="text-center p-2">
                          <input type="checkbox" checked={hasAction(m.key, a)} onChange={() => toggleAction(m.key, a)}
                            className="rounded border-gray-300 cursor-pointer" data-testid={`perm-${m.key}-${a}`} />
                        </td>
                      ))}
                      <td className="text-center p-2">
                        <input type="checkbox" checked={allOn} onChange={() => toggleModuleAll(m.key)}
                          className="rounded border-gray-300 cursor-pointer" data-testid={`perm-${m.key}-all`} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" onClick={handleSave} disabled={saving} data-testid="save-role-btn">
              {saving ? 'Saving...' : editing ? 'Save Changes' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-role-dialog">
          <DialogHeader><DialogTitle className="font-['Plus_Jakarta_Sans']">Delete Role</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B]">Delete the <strong>{deleteConfirm?.name}</strong> role? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-role-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
